import * as React from 'react';
import {
  Toolbar,
  getIntrinsicElementProps,
  mergeCallbacks,
  slot,
  useEventCallback,
  useFocusFinders,
  useId,
} from '@fluentui/react-components';
import { useAnnounce_unstable as useAnnounceContext } from '@fluentui/react-shared-contexts';
import type { UserMessageProps, UserMessageState } from './UserMessage.types';
import { useMessageAttributes } from '../../utils/useMessageAttributesV2';
import { useUserMessagePositioning } from '../../utils/useUserMessagePositioning';
import { useActionBarSlot } from '../../utils/useActionBarSlot';
import { elementContains, useMergedRefs, useTimeout } from '@fluentui/react-utilities';

/**
 * Create the state required to render UserMessage.
 *
 * The returned state can be modified with hooks such as useUserMessageStyles_unstable,
 * before being passed to renderUserMessage_unstable.
 *
 * @param props - props from this instance of UserMessage
 * @param ref - reference to root HTMLElement of UserMessage
 */
export const useUserMessage_unstable = (props: UserMessageProps, ref: React.Ref<HTMLDivElement>): UserMessageState => {
  const { announcement, accessibleHeading, actionBarAccessibleButton, actionBar, message, timestamp } = props;
  const messageId = useId('user-message-', props.id);
  const headingId = `${messageId}-title`;
  const [userMessagePopupRef, userMessageTargetRef] = useUserMessagePositioning(props);

  const { announce } = useAnnounceContext();
  const [setTimeout] = useTimeout();
  const { ...copilotChatAttributes } = useMessageAttributes({ contentId: messageId, headingId });

  const [isHovered, setIsHovered] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  const { findFirstFocusable } = useFocusFinders();

  const rootRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    announcement && announce(announcement, { batchId: messageId });
  }, [announce, announcement, messageId]);

  const state: UserMessageState = {
    components: {
      root: 'div',
      accessibleHeading: 'h5',
      timestamp: 'span',
      actionBarAccessibleButton: 'button',
      actionBar: Toolbar,
      message: 'div',
    },
    root: slot.always(
      getIntrinsicElementProps('div', {
        ref: useMergedRefs(rootRef, ref),
        ...copilotChatAttributes,
        ...props,
      }),
      { elementType: 'div' },
    ),

    accessibleHeading: slot.optional(accessibleHeading, {
      defaultProps: {
        children: 'You said: ',
        id: headingId,
      },
      elementType: 'h5',
      renderByDefault: true,
    }),

    timestamp: slot.optional(timestamp, {
      elementType: 'span',
    }),

    actionBarAccessibleButton: actionBar
      ? slot.optional(actionBarAccessibleButton, {
          defaultProps: {
            'aria-label': 'More actions',
            tabIndex: -1,
          },
          ...copilotChatAttributes,
          elementType: 'button',
          renderByDefault: true,
        })
      : undefined,

    actionBar: useActionBarSlot(actionBar, {
      popoverRef: userMessagePopupRef,
      triggerRef: userMessageTargetRef,
      isFocused,
      isHovered,
    }),

    message: slot.always(message, {
      defaultProps: { children: props.children, id: messageId, ref: userMessageTargetRef },
      elementType: 'div',
    }),
  };

  const handleActionButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // explicitly set isFocused state here for ATs that do not trigger focus on click
    setIsFocused(true);
    setTimeout(() => {
      findFirstFocusable(userMessagePopupRef.current)?.focus();
    }, 0);
  };
  const onActionButtonClick = useEventCallback(
    mergeCallbacks(handleActionButtonClick, state.actionBarAccessibleButton?.onClick),
  );

  if (state.actionBarAccessibleButton) {
    state.actionBarAccessibleButton.onClick = onActionButtonClick;
  }

  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const nextFocusedElement = e.relatedTarget as HTMLElement;
    if (nextFocusedElement !== rootRef.current && !elementContains(rootRef.current, nextFocusedElement)) {
      setIsFocused(false);
    }
  };
  const onFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    setIsFocused(true);
  };
  const onMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(true);
  };
  const onMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsHovered(false);
  };
  state.root.onBlur = useEventCallback(mergeCallbacks(onBlur, props.onBlur));
  state.root.onFocus = useEventCallback(mergeCallbacks(onFocus, props.onFocus));
  state.root.onMouseEnter = useEventCallback(mergeCallbacks(onMouseEnter, props.onMouseEnter));
  state.root.onMouseLeave = useEventCallback(mergeCallbacks(onMouseLeave, props.onMouseLeave));
  return state;
};
